import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Cloud, CheckCircle, XCircle, Wifi, WifiOff, Trash2, Bot, Zap, Plus, Edit3, Save, X, Play, Pause, Clock, Shield, Building2, Key, Eye, EyeOff } from 'lucide-react';
import { settingsApi, tenantsApi } from '../services/api';
import { Card, SectionHeader, Button, Loader, Badge } from '../components/ui';
import toast from 'react-hot-toast';

const PROVIDERS = [
  {
    id: 'AWS',
    name: 'Amazon Web Services',
    short: 'AWS',
    color: '#ff9900',
    gradient: 'linear-gradient(135deg, #ff9900, #e68a00)',
    regions: ['us-east-1', 'us-east-2', 'us-west-1', 'us-west-2', 'eu-west-1', 'eu-central-1', 'ap-southeast-1', 'ap-northeast-1'],
    fields: [
      { key: 'accessKeyId', label: 'Access Key ID', type: 'text' },
      { key: 'secretAccessKey', label: 'Secret Access Key', type: 'password' },
    ],
  },
  {
    id: 'AZURE',
    name: 'Microsoft Azure',
    short: 'Azure',
    color: '#0078d4',
    gradient: 'linear-gradient(135deg, #0078d4, #005a9e)',
    regions: ['eastus', 'eastus2', 'westus', 'westeurope', 'northeurope', 'southeastasia', 'japaneast'],
    fields: [
      { key: 'tenantId', label: 'Tenant ID', type: 'text' },
      { key: 'clientId', label: 'Client ID', type: 'text' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
    ],
  },
  {
    id: 'GCP',
    name: 'Google Cloud Platform',
    short: 'GCP',
    color: '#4285f4',
    gradient: 'linear-gradient(135deg, #4285f4, #ea4335, #fbbc05, #34a853)',
    regions: ['us-central1', 'us-east1', 'us-west1', 'europe-west1', 'asia-east1', 'asia-southeast1'],
    fields: [
      { key: 'projectId', label: 'Project ID', type: 'text' },
      { key: 'serviceAccountKey', label: 'Service Account Key (JSON)', type: 'password' },
    ],
  },
];

export default function SettingsPage() {
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedRegion, setSelectedRegion] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [localConnections, setLocalConnections] = useState([]);
  const qc = useQueryClient();

  const { data: apiConnections } = useQuery({
    queryKey: ['connections'],
    queryFn: () => settingsApi.getConnections().then((r) => r.data),
    retry: false,
    onSuccess: (data) => {
      if (Array.isArray(data)) setLocalConnections(data);
    },
  });

  const connections = localConnections;

  const addMut = useMutation({
    mutationFn: (data) => settingsApi.addConnection(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['connections'] });
    },
    onError: () => {},
  });

  const deleteMut = useMutation({
    mutationFn: (id) => settingsApi.deleteConnection(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['connections'] });
    },
    onError: () => {},
  });

  const isConnected = (providerId) =>
    connections.some((c) => c.provider === providerId && c.status === 'CONNECTED');

  const handleConnect = (provider) => {
    setConnectingProvider(provider.id);
    setFormData({});
    setSelectedRegion(provider.regions[0]);
    setTestResult(null);
  };

  const handleDisconnect = (providerId) => {
    const conn = connections.find((c) => c.provider === providerId);
    if (conn) {
      deleteMut.mutate(conn.id);
      setLocalConnections((prev) => prev.filter((c) => c.provider !== providerId));
    } else {
      setLocalConnections((prev) => prev.filter((c) => c.provider !== providerId));
    }
    toast.success(`${providerId} disconnected`);
  };

  const handleTestConnection = () => {
    setTestResult('testing');
    setTimeout(() => {
      setTestResult('success');
      toast.success('Connection test successful');
    }, 1500);
  };

  const handleSave = () => {
    const provider = PROVIDERS.find((p) => p.id === connectingProvider);
    if (!provider) return;

    const newConn = {
      id: `conn-${Date.now()}`,
      provider: connectingProvider,
      region: selectedRegion,
      status: 'CONNECTED',
      ...formData,
    };

    addMut.mutate(newConn);
    setLocalConnections((prev) => [...prev, newConn]);
    setConnectingProvider(null);
    toast.success(`${provider.name} connected successfully`);
  };

  const handleCancel = () => {
    setConnectingProvider(null);
    setFormData({});
    setTestResult(null);
  };

  const activeProvider = PROVIDERS.find((p) => p.id === connectingProvider);

  return (
    <div>
      <SectionHeader
        title="Settings"
        subtitle="Manage cloud provider connections and application configuration"
      />

      {/* ── Tenant Management ── */}
      <TenantsSection />

      {/* Cloud Connections */}
      <div style={{ color: '#fff', fontWeight: 600, fontSize: 16, marginBottom: 16 }}>
        Cloud Connections
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 16,
        marginBottom: 32,
      }}>
        {PROVIDERS.map((provider) => {
          const connected = isConnected(provider.id);
          return (
            <Card key={provider.id} style={{
              padding: 24,
              border: connected ? `1px solid ${provider.color}40` : '1px solid rgba(255,255,255,0.07)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Accent gradient */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: provider.gradient,
              }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: `${provider.color}18`,
                  border: `1px solid ${provider.color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Cloud size={24} color={provider.color} />
                </div>
                {connected && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '4px 10px', borderRadius: 20,
                    background: 'rgba(0,200,150,0.12)',
                    border: '1px solid rgba(0,200,150,0.3)',
                  }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#00c896' }} />
                    <span style={{ color: '#00c896', fontSize: 11, fontWeight: 600 }}>Connected</span>
                  </div>
                )}
              </div>

              <div style={{ color: '#fff', fontSize: 17, fontWeight: 700, marginBottom: 4 }}>
                {provider.short}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginBottom: 20 }}>
                {provider.name}
              </div>

              {connected ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDisconnect(provider.id)}
                  style={{ width: '100%' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <WifiOff size={14} /> Disconnect
                  </span>
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleConnect(provider)}
                  style={{ width: '100%' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <Wifi size={14} /> Connect
                  </span>
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      {/* Connection Form */}
      {connectingProvider && activeProvider && (
        <Card style={{ marginBottom: 32, border: `1px solid ${activeProvider.color}30` }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 16, marginBottom: 20 }}>
            Connect {activeProvider.name}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 600 }}>
            {/* Region */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                Region
              </label>
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                {activeProvider.regions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            {/* Credential Fields */}
            {activeProvider.fields.map((field) => (
              <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                  {field.label}
                </label>
                <input
                  type={field.type}
                  value={formData[field.key] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff',
                    fontSize: 13,
                    fontFamily: 'inherit',
                    outline: 'none',
                  }}
                />
              </div>
            ))}
          </div>

          {/* Test Result */}
          {testResult && (
            <div style={{
              marginTop: 16, padding: '10px 14px', borderRadius: 8,
              display: 'flex', alignItems: 'center', gap: 8,
              background: testResult === 'success' ? 'rgba(0,200,150,0.1)' : testResult === 'error' ? 'rgba(255,92,92,0.1)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${testResult === 'success' ? 'rgba(0,200,150,0.3)' : testResult === 'error' ? 'rgba(255,92,92,0.3)' : 'rgba(255,255,255,0.1)'}`,
            }}>
              {testResult === 'testing' && <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>Testing connection...</span>}
              {testResult === 'success' && <><CheckCircle size={16} color="#00c896" /><span style={{ color: '#00c896', fontSize: 13, fontWeight: 500 }}>Connection successful</span></>}
              {testResult === 'error' && <><XCircle size={16} color="#ff5c5c" /><span style={{ color: '#ff5c5c', fontSize: 13, fontWeight: 500 }}>Connection failed</span></>}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Button variant="secondary" size="sm" onClick={handleTestConnection}>
              Test Connection
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave}>
              Save Connection
            </Button>
            <Button variant="secondary" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {/* ── AI Agents Management ── */}
      <AgentsSection />

      {/* ── Optimization Rules Management ── */}
      <RulesSection />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AI Agents Management
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_AGENTS = [
  { id: 'cost-analysis', name: 'Cost Analysis Agent', model: 'NLP Intent Detection', schedule: '30s', description: 'Analyses AWS Cost Explorer data in real-time. Detects spending anomalies and forecasts monthly totals.', color: '#0084ff', enabled: true },
  { id: 'rules-optimization', name: 'Rules Optimization Agent', model: 'Pattern Matching Engine', schedule: 'On refresh', description: '40-rule engine evaluating live billing data across compute, storage, network, database, application, and operations.', color: '#00c896', enabled: true },
  { id: 'infra-discovery', name: 'Infrastructure Discovery Agent', model: 'CloudWatch + Organizations API', schedule: '10 min', description: 'Cross-account monitoring via STS AssumeRole. Discovers EC2, RDS, Lambda, S3, ELB across all sub-accounts.', color: '#ff9900', enabled: true },
  { id: 'capacity-planning', name: 'Capacity Planning Agent', model: 'Statistical Forecasting', schedule: '1 hr', description: 'Time-series forecasting with confidence intervals. Predicts CPU, memory, storage growth and flags capacity risks.', color: '#a855f7', enabled: true },
  { id: 'nlp-query', name: 'NLP Query Agent', model: 'GPT-4o', schedule: 'On demand', description: 'Natural language interface for infrastructure queries. 10 intent categories with context-aware responses.', color: '#f472b6', enabled: true },
  { id: 'anomaly-detection', name: 'Anomaly Detection Agent', model: 'Threshold + Trend Analysis', schedule: '5 min', description: 'Monitors metrics for deviations from baselines. Detects CPU spikes, memory leaks, cost anomalies.', color: '#ff5c5c', enabled: true },
];

function AgentsSection() {
  const [agents, setAgents] = useState(DEFAULT_AGENTS);
  const [editingAgent, setEditingAgent] = useState(null);
  const [showNewAgent, setShowNewAgent] = useState(false);
  const [newAgent, setNewAgent] = useState({ name: '', model: '', schedule: '10 min', description: '' });

  const toggleAgent = (id) => {
    setAgents((prev) => prev.map((a) => a.id === id ? { ...a, enabled: !a.enabled } : a));
    const agent = agents.find((a) => a.id === id);
    toast.success(`${agent.name} ${agent.enabled ? 'disabled' : 'enabled'}`);
  };

  const saveEdit = (id, updates) => {
    setAgents((prev) => prev.map((a) => a.id === id ? { ...a, ...updates } : a));
    setEditingAgent(null);
    toast.success('Agent updated');
  };

  const addAgent = () => {
    if (!newAgent.name.trim()) return toast.error('Agent name required');
    const id = 'custom-' + Date.now();
    setAgents((prev) => [...prev, { ...newAgent, id, color: '#4a9eff', enabled: true }]);
    setShowNewAgent(false);
    setNewAgent({ name: '', model: '', schedule: '10 min', description: '' });
    toast.success('Agent created');
  };

  const deleteAgent = (id) => {
    setAgents((prev) => prev.filter((a) => a.id !== id));
    toast.success('Agent deleted');
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 6,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff', fontSize: 13, fontFamily: 'inherit', outline: 'none',
  };
  const labelStyle = { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>AI Agents</div>
        <Button variant="primary" size="sm" onClick={() => setShowNewAgent(!showNewAgent)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> New Agent
        </Button>
      </div>

      {/* New Agent Form */}
      {showNewAgent && (
        <Card style={{ marginBottom: 16, border: '1px solid rgba(0,132,255,0.2)', padding: 20 }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Create New Agent</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Agent Name</label>
              <input style={inputStyle} placeholder="e.g., Compliance Audit Agent" value={newAgent.name} onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>AI Model / Engine</label>
              <input style={inputStyle} placeholder="e.g., GPT-4o, Rules Engine, Statistical" value={newAgent.model} onChange={(e) => setNewAgent({ ...newAgent, model: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Schedule</label>
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={newAgent.schedule} onChange={(e) => setNewAgent({ ...newAgent, schedule: e.target.value })}>
                <option value="On demand">On demand</option>
                <option value="30s">Every 30 seconds</option>
                <option value="1 min">Every 1 minute</option>
                <option value="5 min">Every 5 minutes</option>
                <option value="10 min">Every 10 minutes</option>
                <option value="30 min">Every 30 minutes</option>
                <option value="1 hr">Every 1 hour</option>
                <option value="On refresh">On refresh</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <input style={inputStyle} placeholder="What does this agent do?" value={newAgent.description} onChange={(e) => setNewAgent({ ...newAgent, description: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <Button variant="primary" size="sm" onClick={addAgent}><span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Save size={13} /> Create</span></Button>
            <Button variant="secondary" size="sm" onClick={() => setShowNewAgent(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Agent Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 32 }}>
        {agents.map((agent) => (
          <Card key={agent.id} style={{
            padding: 20, opacity: agent.enabled ? 1 : 0.5,
            borderLeft: `3px solid ${agent.color || '#4a9eff'}`,
            transition: 'opacity 0.2s',
          }}>
            {editingAgent === agent.id ? (
              <AgentEditForm agent={agent} onSave={saveEdit} onCancel={() => setEditingAgent(null)} />
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Bot size={18} color={agent.color} />
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{agent.name}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setEditingAgent(agent.id)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', padding: 4 }}><Edit3 size={14} /></button>
                    {agent.id.startsWith('custom-') && (
                      <button onClick={() => deleteAgent(agent.id)} style={{ background: 'none', border: 'none', color: 'rgba(255,92,92,0.5)', cursor: 'pointer', padding: 4 }}><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>{agent.description}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: 'rgba(168,85,247,0.1)', color: '#a855f7', border: '1px solid rgba(168,85,247,0.2)' }}>
                    {agent.model}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                    <Clock size={10} /> {agent.schedule}
                  </span>
                  <button onClick={() => toggleAgent(agent.id)} style={{
                    marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4,
                    padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    background: agent.enabled ? 'rgba(0,200,150,0.1)' : 'rgba(255,92,92,0.1)',
                    color: agent.enabled ? '#00c896' : '#ff5c5c',
                    border: `1px solid ${agent.enabled ? 'rgba(0,200,150,0.25)' : 'rgba(255,92,92,0.25)'}`,
                  }}>
                    {agent.enabled ? <><Play size={10} /> Active</> : <><Pause size={10} /> Paused</>}
                  </button>
                </div>
              </>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}

function AgentEditForm({ agent, onSave, onCancel }) {
  const [form, setForm] = useState({ name: agent.name, model: agent.model, schedule: agent.schedule, description: agent.description });
  const inputStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 6,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff', fontSize: 13, fontFamily: 'inherit', outline: 'none',
  };
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Agent name" />
        <input style={inputStyle} value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="AI Model" />
        <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })}>
          {['On demand', '30s', '1 min', '5 min', '10 min', '30 min', '1 hr', 'On refresh'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input style={inputStyle} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="primary" size="sm" onClick={() => onSave(agent.id, form)}><span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Save size={12} /> Save</span></Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Optimization Rules Management
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_RULES = [
  // STORAGE
  { id: 'r1', name: 'EBS Snapshot Lifecycle', dimension: 'STORAGE', threshold: 500, enabled: true, builtin: true },
  { id: 'r2', name: 'EBS gp2 → gp3 Migration', dimension: 'STORAGE', threshold: 100, enabled: true, builtin: true },
  { id: 'r3', name: 'S3 Intelligent-Tiering', dimension: 'STORAGE', threshold: 200, enabled: true, builtin: true },
  { id: 'r4', name: 'EBS io1 → io2 Migration', dimension: 'STORAGE', threshold: 200, enabled: true, builtin: true },
  { id: 'r5', name: 'EFS Elastic Throughput', dimension: 'STORAGE', threshold: 150, enabled: true, builtin: true },
  // NETWORK
  { id: 'r6', name: 'NAT → VPC Endpoints', dimension: 'NETWORK', threshold: 200, enabled: true, builtin: true },
  { id: 'r7', name: 'Cross-AZ Data Transfer', dimension: 'NETWORK', threshold: 300, enabled: true, builtin: true },
  { id: 'r8', name: 'Outbound → CloudFront CDN', dimension: 'NETWORK', threshold: 200, enabled: true, builtin: true },
  { id: 'r9', name: 'VPN Consolidation', dimension: 'NETWORK', threshold: 200, enabled: true, builtin: true },
  { id: 'r10', name: 'ELB Right-sizing', dimension: 'NETWORK', threshold: 500, enabled: true, builtin: true },
  { id: 'r11', name: 'CloudFront Cache Optimization', dimension: 'NETWORK', threshold: 300, enabled: true, builtin: true },
  { id: 'r12', name: 'Route53 Query Optimization', dimension: 'NETWORK', threshold: 100, enabled: true, builtin: true },
  { id: 'r13', name: 'PrivateLink Endpoints', dimension: 'NETWORK', threshold: 200, enabled: true, builtin: true },
  { id: 'r14', name: 'Transit Gateway Optimization', dimension: 'NETWORK', threshold: 300, enabled: true, builtin: true },
  { id: 'r15', name: 'WAF Consolidation', dimension: 'NETWORK', threshold: 200, enabled: true, builtin: true },
  // COMPUTE
  { id: 'r16', name: 'Legacy EC2 Upgrade', dimension: 'COMPUTE', threshold: 100, enabled: true, builtin: true },
  { id: 'r17', name: 'On-Demand → Reserved', dimension: 'COMPUTE', threshold: 1000, enabled: true, builtin: true },
  { id: 'r18', name: 'Savings Plans Coverage', dimension: 'COMPUTE', threshold: 2000, enabled: true, builtin: true },
  { id: 'r19', name: 'Graviton Migration', dimension: 'COMPUTE', threshold: 500, enabled: true, builtin: true },
  { id: 'r20', name: 'Spot Fleet for Batch', dimension: 'COMPUTE', threshold: 500, enabled: true, builtin: true },
  { id: 'r21', name: 'Lambda Memory Optimization', dimension: 'COMPUTE', threshold: 200, enabled: true, builtin: true },
  { id: 'r22', name: 'Fargate Rightsizing', dimension: 'COMPUTE', threshold: 400, enabled: true, builtin: true },
  { id: 'r23', name: 'Auto Scaling Tuning', dimension: 'COMPUTE', threshold: 1000, enabled: true, builtin: true },
  // DATABASE
  { id: 'r24', name: 'RDS Optimization', dimension: 'DATABASE', threshold: 500, enabled: true, builtin: true },
  { id: 'r25', name: 'Aurora Migration', dimension: 'DATABASE', threshold: 800, enabled: true, builtin: true },
  { id: 'r26', name: 'RDS Read Replicas', dimension: 'DATABASE', threshold: 500, enabled: true, builtin: true },
  { id: 'r27', name: 'RDS Proxy Connection Pooling', dimension: 'DATABASE', threshold: 500, enabled: true, builtin: true },
  { id: 'r28', name: 'Backup Retention Review', dimension: 'DATABASE', threshold: 200, enabled: true, builtin: true },
  { id: 'r29', name: 'Multi-AZ Review', dimension: 'DATABASE', threshold: 500, enabled: true, builtin: true },
  { id: 'r30', name: 'DynamoDB On-Demand', dimension: 'DATABASE', threshold: 300, enabled: true, builtin: true },
  { id: 'r31', name: 'ElastiCache Graviton', dimension: 'DATABASE', threshold: 200, enabled: true, builtin: true },
  // APPLICATION
  { id: 'r32', name: 'Spring Boot JVM Optimization', dimension: 'APPLICATION', threshold: 300, enabled: true, builtin: true },
  { id: 'r33', name: 'React Frontend CDN', dimension: 'APPLICATION', threshold: 200, enabled: true, builtin: true },
  { id: 'r34', name: 'Python Lambda ARM64', dimension: 'APPLICATION', threshold: 200, enabled: true, builtin: true },
  { id: 'r35', name: 'Container/ECR Optimization', dimension: 'APPLICATION', threshold: 50, enabled: true, builtin: true },
  { id: 'r36', name: 'API Gateway REST → HTTP', dimension: 'APPLICATION', threshold: 100, enabled: true, builtin: true },
  { id: 'r37', name: 'Microservices Architecture', dimension: 'APPLICATION', threshold: 1000, enabled: true, builtin: true },
  { id: 'r38', name: 'Observability Optimization', dimension: 'APPLICATION', threshold: 100, enabled: true, builtin: true },
  // OPERATIONS
  { id: 'r39', name: 'CloudWatch Logs Retention', dimension: 'OPERATIONS', threshold: 100, enabled: true, builtin: true },
  { id: 'r40', name: 'CloudTrail Storage', dimension: 'OPERATIONS', threshold: 100, enabled: true, builtin: true },
];

const DIM_COLORS_SETTINGS = {
  COMPUTE: '#ff5c5c', STORAGE: '#ff9900', NETWORK: '#0084ff',
  DATABASE: '#a855f7', APPLICATION: '#00c896', OPERATIONS: '#ffb800',
};

const DIMENSIONS_LIST = ['ALL', 'STORAGE', 'NETWORK', 'COMPUTE', 'DATABASE', 'APPLICATION', 'OPERATIONS'];

function RulesSection() {
  const [rules, setRules] = useState(DEFAULT_RULES);
  const [filterDim, setFilterDim] = useState('ALL');
  const [editingRule, setEditingRule] = useState(null);
  const [editThreshold, setEditThreshold] = useState('');
  const [showNewRule, setShowNewRule] = useState(false);
  const [newRule, setNewRule] = useState({ name: '', dimension: 'COMPUTE', threshold: 500 });

  const filtered = filterDim === 'ALL' ? rules : rules.filter((r) => r.dimension === filterDim);
  const enabledCount = rules.filter((r) => r.enabled).length;

  const toggleRule = (id) => {
    setRules((prev) => prev.map((r) => r.id === id ? { ...r, enabled: !r.enabled } : r));
  };

  const startEdit = (rule) => {
    setEditingRule(rule.id);
    setEditThreshold(String(rule.threshold));
  };

  const saveThreshold = (id) => {
    const val = parseInt(editThreshold);
    if (isNaN(val) || val < 0) return toast.error('Invalid threshold');
    setRules((prev) => prev.map((r) => r.id === id ? { ...r, threshold: val } : r));
    setEditingRule(null);
    toast.success('Threshold updated');
  };

  const addRule = () => {
    if (!newRule.name.trim()) return toast.error('Rule name required');
    const id = 'custom-' + Date.now();
    setRules((prev) => [...prev, { ...newRule, id, enabled: true, builtin: false }]);
    setShowNewRule(false);
    setNewRule({ name: '', dimension: 'COMPUTE', threshold: 500 });
    toast.success('Custom rule created');
  };

  const deleteRule = (id) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
    toast.success('Rule deleted');
  };

  const inputStyle = {
    padding: '6px 10px', borderRadius: 6, width: 80,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
    color: '#fff', fontSize: 13, fontFamily: 'inherit', outline: 'none', textAlign: 'right',
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>Optimization Rules</div>
          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 2 }}>{enabledCount}/{rules.length} rules active</div>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowNewRule(!showNewRule)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> New Rule
        </Button>
      </div>

      {/* New Rule Form */}
      {showNewRule && (
        <Card style={{ marginBottom: 16, border: '1px solid rgba(0,132,255,0.2)', padding: 20 }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Create Custom Rule</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Rule Name</label>
              <input style={{ ...inputStyle, width: '100%', textAlign: 'left' }} placeholder="e.g., Unused Elastic IPs" value={newRule.name} onChange={(e) => setNewRule({ ...newRule, name: e.target.value })} />
            </div>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Dimension</label>
              <select style={{ ...inputStyle, width: '100%', textAlign: 'left', cursor: 'pointer' }} value={newRule.dimension} onChange={(e) => setNewRule({ ...newRule, dimension: e.target.value })}>
                {DIMENSIONS_LIST.filter((d) => d !== 'ALL').map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Threshold ($)</label>
              <input style={inputStyle} type="number" value={newRule.threshold} onChange={(e) => setNewRule({ ...newRule, threshold: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <Button variant="primary" size="sm" onClick={addRule}><span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Save size={12} /> Create</span></Button>
            <Button variant="secondary" size="sm" onClick={() => setShowNewRule(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Dimension Filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {DIMENSIONS_LIST.map((dim) => {
          const count = dim === 'ALL' ? rules.length : rules.filter((r) => r.dimension === dim).length;
          return (
            <button key={dim} onClick={() => setFilterDim(dim)} style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${filterDim === dim ? (DIM_COLORS_SETTINGS[dim] || '#0084ff') : 'rgba(255,255,255,0.1)'}`,
              background: filterDim === dim ? `${DIM_COLORS_SETTINGS[dim] || '#0084ff'}18` : 'transparent',
              color: filterDim === dim ? (DIM_COLORS_SETTINGS[dim] || '#4a9eff') : 'rgba(255,255,255,0.4)',
              fontFamily: 'inherit', transition: 'all 0.15s',
            }}>
              {dim} ({count})
            </button>
          );
        })}
      </div>

      {/* Rules Table */}
      <Card style={{ marginBottom: 32, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Status', 'Rule Name', 'Dimension', 'Threshold', 'Actions'].map((h) => (
                <th key={h} style={{
                  textAlign: h === 'Threshold' ? 'right' : 'left', padding: '10px 14px',
                  color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((rule) => {
              const dimColor = DIM_COLORS_SETTINGS[rule.dimension] || '#888';
              return (
                <tr key={rule.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', opacity: rule.enabled ? 1 : 0.4 }}>
                  <td style={{ padding: '10px 14px', width: 60 }}>
                    <button onClick={() => toggleRule(rule.id)} style={{
                      width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: rule.enabled ? '#00c896' : 'rgba(255,255,255,0.15)',
                      position: 'relative', transition: 'background 0.2s',
                    }}>
                      <span style={{
                        position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%',
                        background: '#fff', transition: 'left 0.2s',
                        left: rule.enabled ? 18 : 2,
                      }} />
                    </button>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>{rule.name}</div>
                    {!rule.builtin && <span style={{ fontSize: 10, color: '#4a9eff' }}>CUSTOM</span>}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                      background: `${dimColor}18`, color: dimColor, border: `1px solid ${dimColor}33`,
                    }}>{rule.dimension}</span>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    {editingRule === rule.id ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        $<input style={inputStyle} value={editThreshold} onChange={(e) => setEditThreshold(e.target.value)} autoFocus onKeyDown={(e) => e.key === 'Enter' && saveThreshold(rule.id)} />
                        <button onClick={() => saveThreshold(rule.id)} style={{ background: 'none', border: 'none', color: '#00c896', cursor: 'pointer' }}><Save size={14} /></button>
                        <button onClick={() => setEditingRule(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' }}><X size={14} /></button>
                      </span>
                    ) : (
                      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontFamily: 'monospace' }}>${rule.threshold}</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => startEdit(rule)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', padding: 4 }} title="Edit threshold"><Edit3 size={13} /></button>
                      {!rule.builtin && (
                        <button onClick={() => deleteRule(rule.id)} style={{ background: 'none', border: 'none', color: 'rgba(255,92,92,0.5)', cursor: 'pointer', padding: 4 }} title="Delete rule"><Trash2 size={13} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tenant Management
// ─────────────────────────────────────────────────────────────────────────────

function TenantsSection() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', awsAccessKeyId: '', awsSecretAccessKey: '', awsRegion: 'us-east-1', awsAccountId: '', description: '', contactEmail: '' });
  const [showSecret, setShowSecret] = useState(false);

  const { data: tenants = [] } = useQuery({
    queryKey: ['tenants-list'],
    queryFn: () => tenantsApi.getAll().then((r) => r.data).catch(() => []),
  });

  const createMut = useMutation({
    mutationFn: (data) => tenantsApi.create(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tenants-list'] }); setShowForm(false); resetForm(); toast.success('Tenant created'); },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to create tenant'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => tenantsApi.update(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tenants-list'] }); setEditingId(null); resetForm(); toast.success('Tenant updated'); },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to update'),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => tenantsApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tenants-list'] }); toast.success('Tenant deactivated'); },
  });

  const testMut = useMutation({
    mutationFn: (id) => tenantsApi.testAws(id),
    onSuccess: () => toast.success('AWS connection successful'),
    onError: () => toast.error('AWS connection failed'),
  });

  const resetForm = () => setForm({ name: '', slug: '', awsAccessKeyId: '', awsSecretAccessKey: '', awsRegion: 'us-east-1', awsAccountId: '', description: '', contactEmail: '' });

  const handleCreate = () => createMut.mutate(form);
  const handleUpdate = () => updateMut.mutate({ id: editingId, data: form });

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, slug: t.slug, awsAccessKeyId: '', awsSecretAccessKey: '', awsRegion: t.awsRegion || 'us-east-1', awsAccountId: t.awsAccountId || '', description: t.description || '', contactEmail: t.contactEmail || '' });
    setShowForm(true);
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff', fontSize: 13, fontFamily: 'inherit', outline: 'none',
  };
  const labelStyle = { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 };

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>Tenants</div>
          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 2 }}>{tenants.length} tenant(s) configured</div>
        </div>
        <Button variant="primary" size="sm" onClick={() => { resetForm(); setEditingId(null); setShowForm(!showForm); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Add Tenant
        </Button>
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <Card style={{ marginBottom: 16, border: '1px solid rgba(255,153,0,0.2)', padding: 24 }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 16 }}>
            {editingId ? 'Edit Tenant' : 'Add New Tenant'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>Tenant Name *</label>
              <input style={inputStyle} placeholder="e.g., Acme Corp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-') })} />
            </div>
            <div>
              <label style={labelStyle}>Slug (URL-safe) *</label>
              <input style={inputStyle} placeholder="e.g., acme-corp" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} />
            </div>
            <div>
              <label style={labelStyle}>AWS Access Key ID</label>
              <input style={inputStyle} placeholder="AKIA..." value={form.awsAccessKeyId} onChange={(e) => setForm({ ...form, awsAccessKeyId: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>AWS Secret Access Key</label>
              <div style={{ position: 'relative' }}>
                <input style={inputStyle} type={showSecret ? 'text' : 'password'} placeholder="Secret key (encrypted at rest)" value={form.awsSecretAccessKey} onChange={(e) => setForm({ ...form, awsSecretAccessKey: e.target.value })} />
                <button onClick={() => setShowSecret(!showSecret)} style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' }}>
                  {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>AWS Region</label>
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.awsRegion} onChange={(e) => setForm({ ...form, awsRegion: e.target.value })}>
                {['us-east-1','us-east-2','us-west-1','us-west-2','eu-west-1','eu-central-1','ap-south-1','ap-southeast-1','ap-northeast-1'].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>AWS Account ID</label>
              <input style={inputStyle} placeholder="123456789012" value={form.awsAccountId} onChange={(e) => setForm({ ...form, awsAccountId: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <input style={inputStyle} placeholder="Brief description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Contact Email</label>
              <input style={inputStyle} placeholder="admin@client.com" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            {editingId ? (
              <Button variant="primary" size="sm" onClick={handleUpdate} disabled={updateMut.isPending}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Save size={13} /> Update</span>
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={handleCreate} disabled={createMut.isPending}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Save size={13} /> Create Tenant</span>
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
          </div>
          {!editingId && (
            <div style={{ marginTop: 10, color: 'rgba(255,255,255,0.25)', fontSize: 11 }}>
              <Key size={10} style={{ display: 'inline', marginRight: 4 }} />
              AWS credentials are encrypted with AES-256-GCM before storage.
            </div>
          )}
        </Card>
      )}

      {/* Tenant Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
        {tenants.map((t) => (
          <Card key={t.id} style={{
            padding: 20,
            borderLeft: `3px solid ${t.active !== false ? '#ff9900' : 'rgba(255,255,255,0.1)'}`,
            opacity: t.active !== false ? 1 : 0.5,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>{t.name}</div>
                <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, fontFamily: 'monospace' }}>{t.slug}</div>
              </div>
              <span style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                background: t.active !== false ? 'rgba(0,200,150,0.12)' : 'rgba(255,92,92,0.12)',
                color: t.active !== false ? '#00c896' : '#ff5c5c',
              }}>
                {t.active !== false ? 'Active' : 'Inactive'}
              </span>
            </div>
            {t.awsAccountId && (
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginBottom: 4 }}>
                AWS Account: <span style={{ color: '#ff9900', fontFamily: 'monospace' }}>{t.awsAccountId}</span>
              </div>
            )}
            {t.awsRegion && (
              <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginBottom: 4 }}>
                Region: {t.awsRegion}
              </div>
            )}
            {t.description && (
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginBottom: 8 }}>{t.description}</div>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <button onClick={() => testMut.mutate(t.id)} disabled={testMut.isPending} style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: 'rgba(0,200,150,0.08)', color: '#00c896', border: '1px solid rgba(0,200,150,0.2)', fontFamily: 'inherit',
              }}>
                Test AWS
              </button>
              <button onClick={() => startEdit(t)} style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: 'rgba(0,132,255,0.08)', color: '#4a9eff', border: '1px solid rgba(0,132,255,0.2)', fontFamily: 'inherit',
              }}>
                Edit
              </button>
              <button onClick={() => deleteMut.mutate(t.id)} style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: 'rgba(255,92,92,0.08)', color: '#ff5c5c', border: '1px solid rgba(255,92,92,0.2)', fontFamily: 'inherit',
              }}>
                Deactivate
              </button>
            </div>
          </Card>
        ))}
        {tenants.length === 0 && (
          <Card style={{ padding: 40, gridColumn: '1 / -1', textAlign: 'center' }}>
            <Building2 size={32} color="rgba(255,255,255,0.15)" style={{ margin: '0 auto 12px' }} />
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>No tenants configured. Click "Add Tenant" to get started.</div>
          </Card>
        )}
      </div>
    </div>
  );
}
